import { LineChart, Line, ResponsiveContainer } from "recharts"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/cn"
import type { SparklinePoint } from "@/types/analytics_escalations"

// Compact KPI card used in the Escalations dashboard top row.
// - Uppercase muted label
// - Large value
// - Delta chip on the right (color depends on direction + tone)
// - Tiny recharts sparkline at the bottom (no axes/grid/tooltip)
//
// Tone semantics:
//   "neutral" / "danger"  → up = red, down = green   (more = worse)
//   "success"             → up = green, down = red   (more = better)
//   "warning"             → up = warning, down = neutral
type Tone = "neutral" | "success" | "danger" | "warning"

type Props = {
  label: string
  value: string
  deltaPct: number | null
  sparkline: SparklinePoint[]
  tone?: Tone
}

export function EscalationMetricCard({ label, value, deltaPct, sparkline, tone = "neutral" }: Props) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-app-muted">
          {label}
        </div>
        <DeltaChip deltaPct={deltaPct} tone={tone} />
      </div>
      <div className="text-2xl font-semibold tracking-tight text-app-text">{value}</div>
      <div className="mt-3 h-10">
        {sparkline.length > 1 ? (
          <ResponsiveContainer width="100%" height={40}>
            <LineChart data={sparkline} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
              <Line
                type="monotone"
                dataKey="count"
                stroke="currentColor"
                strokeWidth={1.75}
                dot={false}
                isAnimationActive={false}
                className="text-brand-red"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full rounded-control bg-white/[0.03]" />
        )}
      </div>
    </Card>
  )
}

function DeltaChip({ deltaPct, tone }: { deltaPct: number | null; tone: Tone }) {
  if (deltaPct === null || Number.isNaN(deltaPct)) {
    return <span className="text-xs text-app-subtle">—</span>
  }
  const rounded = Math.round(deltaPct * 10) / 10
  const isUp = rounded > 0
  const isDown = rounded < 0
  const arrow = isUp ? "↑" : isDown ? "↓" : "→"
  const abs = Math.abs(rounded)

  // Color resolution: tone determines whether "up" is good or bad.
  let cls = "text-app-muted"
  if (rounded === 0) {
    cls = "text-app-muted"
  } else if (tone === "success") {
    cls = isUp ? "text-status-success" : "text-status-error"
  } else if (tone === "warning") {
    cls = isUp ? "text-status-warning" : "text-app-muted"
  } else {
    // neutral / danger → up is bad
    cls = isUp ? "text-status-error" : "text-status-success"
  }

  return (
    <span className={cn("inline-flex items-center gap-0.5 rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[11px] font-medium", cls)}>
      {arrow}{abs}%
    </span>
  )
}
